import { useCallback } from 'react';
import { useAppDispatch } from '../grid/reduxStore/Store';
import { useSocketClient } from '../../../providers/SocketContext';
import { useCheckDocumentLockStatus } from 'components/editor/hooks/useCheckDocumentLockStatus';
import { UndoRedoManagerCommandVersion } from '../UndoRedo/UndoRedoManagerCommandVersion';
import { UpdateSectionCommand } from '../UndoRedo/UpdateSectionCommand';
import type { Section } from '../Sections/Section.model';
import { detectReordering } from '../SectionsDrawer/reorderUtils.ts';
import type { OnReorderFn } from '../SectionsDrawer/types.ts';

export const useReorderSectionsHandler = (sections: Section[]): { reorder: OnReorderFn } => {
  const dispatch = useAppDispatch();
  const { socketClient } = useSocketClient();
  const checkDocumentLockStatus = useCheckDocumentLockStatus();

  const reorder = useCallback(
    (newSectionsSequence: { id: string }[]) => {
      const { whatMovedId, afterWhatId } = detectReordering(sections, newSectionsSequence);
      const previousIndex = sections.findIndex((s) => s.id === whatMovedId);
      const previousAfterWhatId = previousIndex > 0 ? sections[previousIndex - 1].id : null;

      const command = new UpdateSectionCommand(
        { dispatch, socketClient, checkDocumentLockStatus },
        {
          sectionId: whatMovedId,
          previousState: { id: whatMovedId, moveAfterSectionId: previousAfterWhatId },
          newState: { id: whatMovedId, moveAfterSectionId: afterWhatId },
        }
      );

      command.execute();

      const undoRedoManager = UndoRedoManagerCommandVersion.getUndoRedoManager();
      undoRedoManager.pushUndoRedoCommands(command);
    },
    [dispatch, socketClient, checkDocumentLockStatus, sections]
  );

  return { reorder };
};
